import { TreeItem, TreeItemIndex } from 'react-complex-tree';

import { TQModelAccountsCount, TQModelTreeNode } from 'types/tqModelTree';
import i18n from 'utils/i18n';

const t = i18n.t;

const ROOT_ID = 'root';
export const UNASSIGNED_ID = '__unassigned__';
export const TQModelTreeHelper = {
  getTreeDataFromTQModelNodes(
    tqModelNodes: TQModelTreeNode[] | undefined,
    tqModelAccountsCounts: TQModelAccountsCount[] | undefined,
    includeUnassigned: boolean,
  ): Record<TreeItemIndex, TreeItem<TQModelTreeNode>> {
    // Define baseline tree data structure with the root defined
    const treeData: Record<TreeItemIndex, TreeItem<TQModelTreeNode>> = {
      root: {
        index: ROOT_ID,
        canMove: false,
        isFolder: tqModelNodes && tqModelNodes.length > 0,
        children: [],
        data: {
          id: ROOT_ID,
          num_accounts: undefined,
          is_root: true,
          index: ROOT_ID,
          children: [],
          is_ancestor: false,
          siblings: [],
        },
        canRename: false,
      },
    };
    if (includeUnassigned) {
      const unassignedAccounts = tqModelAccountsCounts?.find((entry) => entry.territory_uuid === UNASSIGNED_ID);

      treeData[t('TQModelView.unassigned_accounts')] = {
        index: t('TQModelView.unassigned_accounts'),
        canMove: false,
        isFolder: false,
        children: [],
        data: {
          id: UNASSIGNED_ID,
          num_accounts: unassignedAccounts?.accounts_count,
          compare_num_accounts: unassignedAccounts?.draft_accounts_count,
          is_root: false,
          index: t('TQModelView.unassigned_accounts'),
          is_ancestor: false,
          children: [],
          siblings: [],
        },
        canRename: false,
      };
    }

    if (tqModelNodes === undefined) {
      return treeData;
    }

    // Convert accounts counts to a map for quick access
    const accountsCountsMap =
      tqModelAccountsCounts?.reduce(
        (result, current) => ({
          ...result,
          [current.territory_uuid]: current,
        }),
        {} as Record<string, TQModelAccountsCount>,
      ) ?? ({} as Record<string, TQModelAccountsCount>);

    tqModelNodes.forEach((node) => {
      treeData[node.index] = {
        index: node.index,
        canMove: false,
        isFolder: node.children?.length > 0,
        children: node.children,
        data: {
          ...node,
          num_accounts: accountsCountsMap[node.id]?.accounts_count,
          compare_num_accounts: accountsCountsMap[node.id]?.draft_accounts_count,
        },
        canRename: true,
      };

      if (node.is_root) {
        treeData.root.children?.push(node.index);
      }
    });

    // Add the 'Unassigned' accounts node to the root level
    if (includeUnassigned) {
      treeData.root.children?.push(t('TQModelView.unassigned_accounts'));
    }

    return treeData;
  },
  getNodeParent(
    tqTreeData: Record<TreeItemIndex, TreeItem<TQModelTreeNode>>,
    nodeIndex: TreeItemIndex,
  ): TreeItem<TQModelTreeNode> | null {
    const data = Object.entries(tqTreeData);
    const parentNode = data.find(([_, item]) => (item && item.children?.includes(nodeIndex)) || false);
    return (parentNode && parentNode[1]) || null;
  },

  getListOfNodes(
    treeData: Record<TreeItemIndex, TreeItem<TQModelTreeNode>>,
    excludedIds?: Array<string>,
    sortList?: boolean,
  ): TQModelTreeNode[] {
    if (!treeData) {
      return [];
    }

    const nodes = Object.values(treeData);
    // 'root' and 'unassigned' are internal nodes only
    const defaultExclusions = [ROOT_ID, UNASSIGNED_ID];
    let exclusions = defaultExclusions;

    if (excludedIds) {
      exclusions = [...excludedIds, ...defaultExclusions];
    }
    const filtered = nodes.filter((n) => !exclusions.includes(n.data.id));
    const treeNodes = filtered.map((f) => f.data);
    if (!sortList) {
      return treeNodes;
    }

    return treeNodes.sort((a, b) => {
      if (a.index < b.index) return -1;
      if (a.index > b.index) return 1;
      return 0;
    });
  },

  getDescendants(treeData: Record<TreeItemIndex, TreeItem<TQModelTreeNode>>, node: TQModelTreeNode): TQModelTreeNode[] {
    // We need to filter out undefined entries. There is a race condition where node is defined while treeData
    // hasn't been updated and that causes `child.data` to fail in the descendants variable creation.
    // This is a bandaid to unblock the problem, but will buy us time while we figure out why treeData is stale.
    const children = node.children
      .map((childIndex) => treeData[childIndex])
      .filter((treeNode: TreeItem<TQModelTreeNode>) => !!treeNode);
    const descendants = children.map((child) => child.data);

    children.forEach((child) => {
      const grandChildren = this.getDescendants(treeData, child.data);
      grandChildren.forEach((descendant) => descendants.push(descendant));
    });

    return descendants;
  },
};
